import * as React from 'react';
import { useDispatch } from 'react-redux';
import Modal from 'antd/lib/modal';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { InfoCircleOutlined } from '@ant-design/icons';
import { antGrey } from '@/components/styles/globalStyles';
import { TRY_ADD_ISSUE_SOURCES, tryAddIssueSources } from '@/actions/triage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { IssueSourceRef } from '@/types';
import { AbsorbableSourcesSelect, ATTACH_SIGNALS_TOOLTIP } from './IssueCandidatesSelect';

interface AddSourceModalProps {
    open: boolean;
    onClose: () => void;
    orgId: string;
    appId: string;
    issueId: string;
}

export const AddSourceModal: React.FC<AddSourceModalProps> = ({ open, onClose, orgId, appId, issueId }) => {
    const dispatch = useDispatch();
    const [selectedSources, setSelectedSources] = React.useState<IssueSourceRef[]>([]);

    const isSaving = useIsLoading(TRY_ADD_ISSUE_SOURCES, issueId);
    const saveError = useError(TRY_ADD_ISSUE_SOURCES, issueId);
    const saveSucceeded = useUpdated(TRY_ADD_ISSUE_SOURCES, issueId);

    React.useEffect(() => {
        if (saveSucceeded) {
            setSelectedSources([]);
            onClose();
        }
    }, [saveSucceeded]);

    const onSubmit = () => {
        dispatch(tryAddIssueSources({ orgId, appId, issueId, sources: selectedSources }));
    };

    return (
        <Modal
            title="Pull in an existing signal"
            open={open}
            onCancel={onClose}
            onOk={onSubmit}
            okText={selectedSources.length > 1 ? 'Pull in signals' : 'Pull in signal'}
            okButtonProps={{ disabled: selectedSources.length === 0 }}
            confirmLoading={isSaving}
            destroyOnClose
            width={560}
        >
            {saveError && <Alert type="error" message={saveError} showIcon style={{ marginBottom: 16 }} />}
            <Text style={{ display: 'block', marginBottom: 12 }}>
                Pull in a signal from another issue (as long as it is not yet associated with a fix).{' '}
                <Tooltip title={ATTACH_SIGNALS_TOOLTIP}>
                    <InfoCircleOutlined style={{ color: antGrey.grey7 }} />
                </Tooltip>
            </Text>
            <AbsorbableSourcesSelect
                orgId={orgId}
                appId={appId}
                open={open}
                excludeIssueIds={[issueId]}
                value={selectedSources}
                onChange={setSelectedSources}
            />
        </Modal>
    );
};
